import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, Timestamp, query, where } from "firebase/firestore";

interface Chapter {
  id: string;
  class: string;
  chapterName: string;
}

interface Topic {
  id: string;
  class: string;
  chapterId: string;
  topicName: string;
} 

const ResourceManagement = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resources, setResources] = useState<any[]>([]);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [resourceClass, setResourceClass] = useState("");
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [resourceType, setResourceType] = useState<'file' | 'video'>('file');
  const [videoLink, setVideoLink] = useState<string>("");

  useEffect(() => {
    fetchResources();
  }, []);

  // Fetch chapters for selected class
  useEffect(() => {
    if (!resourceClass) {
      setChapters([]);
      setSelectedChapter("");
      setTopics([]);
      setSelectedTopic("");
      return;
    }
    const fetchChapters = async () => {
      setChaptersLoading(true);
      try {
        const q = query(collection(db, "chapters"), where("class", "==", resourceClass));
        const snapshot = await getDocs(q);
        setChapters(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
      } catch {
        setChapters([]);
      }
      setChaptersLoading(false);
    };
    fetchChapters();
    setSelectedChapter("");
    setTopics([]);
    setSelectedTopic("");
  }, [resourceClass]);

  // Fetch topics for selected chapter
  useEffect(() => {
    if (!selectedChapter) {
      setTopics([]);
      setSelectedTopic("");
      return;
    }
    const fetchTopics = async () => {
      setTopicsLoading(true);
      try {
        const q = query(collection(db, "topics"), where("class", "==", resourceClass), where("chapterId", "==", selectedChapter));
        const snapshot = await getDocs(q);
        setTopics(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
      } catch {
        setTopics([]);
      }
      setTopicsLoading(false);
    };
    fetchTopics();
    setSelectedTopic("");
  }, [selectedChapter, resourceClass]);


  const fetchResources = async () => {
    const querySnapshot = await getDocs(collection(db, "resources"));
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setResources(data);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!title || !resourceClass || !selectedChapter || !selectedTopic || (resourceType === 'file' && !file) || (resourceType === 'video' && !videoLink)) {
      setError("Please provide all required fields.");
      return;
    }
    setUploading(true);
    setProgress(0);

    const chapterObj = chapters.find(ch => ch.id === selectedChapter);
    const topicObj = topics.find(t => t.id === selectedTopic);

    if (resourceType === 'video') {
      try {
        await addDoc(collection(db, "resources"), {
          title,
          desc,
          class: resourceClass,
          chapterId: selectedChapter,
          chapterName: chapterObj?.chapterName || "",
          topicId: selectedTopic,
          topicName: topicObj?.topicName || "",
          url: videoLink,
          filename: '',
          uploadedAt: Timestamp.now(),
          resourceType: 'video',
        });
        setUploading(false);
        setFile(null);
        setTitle("");
        setDesc("");
        setResourceClass("");
        setVideoLink("");
        setProgress(0);
        setShowModal(false);
        fetchResources();
      } catch (err) {
        setError("Upload failed. Try again.");
        setUploading(false);
      }
      return;
    }

    // === Cloudinary Credentials ===
    // Edit these values directly:
    const cloudName = "dhxiqrn0d"; // <-- Put your Cloudinary cloud name here
    const uploadPreset = "marazsir"; // <-- Put your unsigned upload preset here
    // ==============================

    const url = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;
    const formData = new FormData();
    formData.append("file", file!);
    formData.append("upload_preset", uploadPreset);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = async () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          const uploadedUrl = response.secure_url;
          await addDoc(collection(db, "resources"), {
            title,
            desc,
            class: resourceClass,
            chapterId: selectedChapter,
            chapterName: chapterObj?.chapterName || "",
            topicId: selectedTopic,
            topicName: topicObj?.topicName || "",
            url: uploadedUrl,
            filename: file!.name,
            uploadedAt: Timestamp.now(),
            resourceType: 'file',
          });
          setUploading(false);
          setFile(null);
          setTitle("");
          setDesc("");
          setResourceClass("");
          setVideoLink("");
          setProgress(0);
          setShowModal(false);
          fetchResources();
        } else {
          // Enhanced error logging
          let errorMsg = `Upload failed (status: ${xhr.status}).`;
          if (xhr.responseText) {
            errorMsg += `\nCloudinary response: ${xhr.responseText}`;
          }
          setError(errorMsg);
          setUploading(false);
          // Also log to console for devs
          console.error("Cloudinary upload error:", xhr.status, xhr.responseText);
        }
      };
      xhr.onerror = () => {
        setError("Upload failed. Try again.");
        setUploading(false);
      };
      xhr.send(formData);
    } catch (err) {
      setError("Upload failed. Try again.");
      setUploading(false);
    }
  };


  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded shadow mt-8">

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Resource Management</h2>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          onClick={() => setShowModal(true)}
        >
          Add New Item
        </button>
      </div>

      {/* Modal for Add Resource */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-gray-500"
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            <h3 className="text-xl font-semibold mb-4">Add New Resource</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              {/* Resource Type Selector */}
              <div className="flex gap-4 items-center">
                <label className="font-semibold">Resource Type:</label>
                <select
                  className="border px-3 py-2 rounded"
                  value={resourceType}
                  onChange={e => setResourceType(e.target.value as 'file' | 'video')}
                  required
                >
                  <option value="file">Image / Doc / PDF</option>
                  <option value="video">Video Link</option>
                </select>
              </div>
              <input
                type="text"
                placeholder="Resource Title"
                className="w-full border px-3 py-2 rounded"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <textarea
                placeholder="Description (optional)"
                className="w-full border px-3 py-2 rounded"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />
              {/* Class Dropdown */}
              <select
                className="w-full border px-3 py-2 rounded"
                value={resourceClass}
                onChange={e => setResourceClass(e.target.value)}
                required
              >
                <option value="">Select Class</option>
                <option value="IX">Class IX</option>
                <option value="X">Class X</option>
                <option value="XI">Class XI</option>
                <option value="XII">Class XII</option>
              </select>
              {/* Chapter Dropdown */}
              {resourceClass && (
                <select
                  className="w-full border px-3 py-2 rounded mt-2"
                  value={selectedChapter}
                  onChange={e => setSelectedChapter(e.target.value)}
                  required
                  disabled={chaptersLoading}
                >
                  <option value="">{chaptersLoading ? "Loading chapters..." : "Select Chapter"}</option>
                  {chapters.map(ch => (
                    <option key={ch.id} value={ch.id}>{ch.chapterName}</option>
                  ))}
                </select>
              )}
              {/* Topic Dropdown */}
              {resourceClass && selectedChapter && (
                <select
                  className="w-full border px-3 py-2 rounded mt-2"
                  value={selectedTopic}
                  onChange={e => setSelectedTopic(e.target.value)}
                  required
                  disabled={topicsLoading}
                >
                  <option value="">{topicsLoading ? "Loading topics..." : "Select Topic"}</option>
                  {topics.map(topic => (
                    <option key={topic.id} value={topic.id}>{topic.topicName}</option>
                  ))}
                </select>
              )}
              {/* File or Video Link Input */}
              {resourceType === 'file' ? (
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full"
                  required
                />
              ) : (
                <input
                  type="url"
                  placeholder="Paste video link (YouTube, Vimeo, etc.)"
                  className="w-full border px-3 py-2 rounded"
                  value={videoLink}
                  onChange={e => setVideoLink(e.target.value)}
                  required
                />
              )}
              {error && <div className="text-red-600">{error}</div>}
              {uploading && (
                <div className="w-full bg-gray-200 rounded h-2 mb-2">
                  <div
                    className="bg-blue-500 h-2 rounded"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              )}
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full"
                disabled={uploading}
              >
                {uploading ? `Uploading... (${progress}%)` : "Upload Resource"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-2">Uploaded Resources</h3>
        {resources.length === 0 ? (
          <div>No resources uploaded yet.</div>
        ) : (
          <div>
            {['IX', 'X', 'XI', 'XII'].map(cls => {
              const classResources = resources.filter(res => res.class === cls);
              if (classResources.length === 0) return null;
              return (
                <div key={cls} className="mb-8">
                  <h4 className="font-semibold text-blue-700 mb-2">Class {cls}</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border text-sm mb-2">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border px-4 py-2">Title</th>
                          <th className="border px-4 py-2">Description</th>
                          <th className="border px-4 py-2">Topic Name</th>
                          <th className="border px-4 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classResources.map(res => (
                          <tr key={res.id}>
                            <td className="border px-4 py-2">{res.title}</td>
                            <td className="border px-4 py-2">{res.desc}</td>
                            <td className="border px-4 py-2">{res.topicName || '-'}</td>
                            <td className="border px-4 py-2">
                              <a
                                href={res.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700 transition mr-2"
                              >
                                Download
                              </a>
                              <button
                                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700 transition"
                                onClick={async () => {
                                  if (!window.confirm('Are you sure you want to delete this resource?')) return;
                                  try {
                                    // Delete from Firestore
                                    await import('firebase/firestore').then(({ doc, deleteDoc }) =>
                                      deleteDoc(doc(require('../../firebase').db, 'resources', res.id))
                                    );
                                    // Try to delete from Cloudinary (client-side only possible with delete_token, not public_id)
                                    // Extract public_id from URL (e.g. https://res.cloudinary.com/{cloudName}/.../upload/v1234567/<public_id>.<ext>)
                                    const match = res.url.match(/\/upload\/.*?\/([^/.]+)\.[a-zA-Z0-9]+$/);
                                    const publicId = match ? match[1] : null;
                                    if (publicId) {
                                      // Warn: true Cloudinary delete requires API secret (backend), not safe from frontend
                                      alert('Deleted from Firestore. To fully delete from Cloudinary, use the Cloudinary dashboard or implement a backend API.');
                                    }
                                    fetchResources();
                                  } catch (err) {
                                    const errorMsg = (err && typeof err === 'object' && 'message' in err) ? (err as any).message : String(err);
                                    alert('Failed to delete resource: ' + errorMsg);
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceManagement;
